import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FlaskConical, Plus, Trash2, GripVertical, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../services/api';
import { toast } from 'sonner';

export const SensoryAdminPage = ({ sensoryType = 'hotovky' }) => {
  const navigate = useNavigate();
  const [testFields, setTestFields] = useState([]);
  const [evalFields, setEvalFields] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const typeLabel = sensoryType === 'hotovky' ? 'Hotovky' : 'Krabičky';
  
  // New field forms
  const [newTestField, setNewTestField] = useState({ name: '', field_type: 'text', required: false, default_today: false });
  const [newEvalField, setNewEvalField] = useState({ name: '' });

  useEffect(() => {
    fetchData();
  }, [sensoryType]);

  const fetchData = async () => {
    try {
      const [testRes, evalRes] = await Promise.all([
        api.get('/sensory/test-fields', { params: { sensory_type: sensoryType } }),
        api.get('/sensory/eval-fields', { params: { sensory_type: sensoryType } })
      ]);
      setTestFields(testRes.data);
      setEvalFields(evalRes.data);
    } catch (error) {
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  // Test Fields handlers
  const handleAddTestField = async () => {
    if (!newTestField.name.trim()) {
      toast.error('Zadejte název pole');
      return;
    }
    try {
      const order = testFields.length;
      await api.post('/sensory/test-fields', { ...newTestField, order, is_active: true, sensory_type: sensoryType });
      toast.success('Pole přidáno');
      setNewTestField({ name: '', field_type: 'text', required: false, default_today: false });
      fetchData();
    } catch (error) {
      toast.error('Chyba při přidávání pole');
    }
  };

  const handleUpdateTestField = async (field) => {
    try {
      await api.put(`/sensory/test-fields/${field.id}`, field);
      toast.success('Pole aktualizováno');
    } catch (error) {
      toast.error('Chyba při aktualizaci');
    }
  };

  const handleDeleteTestField = async (id) => {
    if (!window.confirm('Opravdu chcete smazat toto pole?')) return;
    try {
      await api.delete(`/sensory/test-fields/${id}`);
      toast.success('Pole smazáno');
      fetchData();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
  };

  // Eval Fields handlers
  const handleAddEvalField = async (isGroupA = false) => {
    if (!newEvalField.name.trim()) {
      toast.error('Zadejte název pole');
      return;
    }
    try {
      const order = evalFields.length;
      await api.post('/sensory/eval-fields', { 
        ...newEvalField, 
        order, 
        is_active: true, 
        sensory_type: sensoryType,
        is_group_a_only: isGroupA 
      });
      toast.success('Pole přidáno');
      setNewEvalField({ name: '' });
      fetchData();
    } catch (error) {
      toast.error('Chyba při přidávání pole');
    }
  };

  const handleUpdateEvalField = async (field) => {
    try {
      await api.put(`/sensory/eval-fields/${field.id}`, field);
      toast.success('Pole aktualizováno');
    } catch (error) {
      toast.error('Chyba při aktualizaci');
    }
  };

  const handleDeleteEvalField = async (id) => {
    if (!window.confirm('Opravdu chcete smazat toto pole?')) return;
    try {
      await api.delete(`/sensory/eval-fields/${id}`);
      toast.success('Pole smazáno');
      fetchData();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Senzorika {typeLabel} - nastavení</h1>
            <p className="text-slate-500">Konfigurace polí pro senzorické zkoušky {typeLabel}</p>
          </div>
        </div>

        {/* Test Fields Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
              <FlaskConical className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Pole pro založení zkoušky</h2>
              <p className="text-sm text-slate-500">Údaje vyplňované při vytváření nové zkoušky</p>
            </div>
          </div>

          {/* Existing test fields */}
          <div className="space-y-3 mb-6">
            {testFields.map((field) => (
              <div key={field.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg group">
                <GripVertical className="h-4 w-4 text-slate-400" />
                <Input
                  value={field.name}
                  onChange={(e) => {
                    const updated = testFields.map(f => 
                      f.id === field.id ? { ...f, name: e.target.value } : f
                    );
                    setTestFields(updated);
                  }}
                  className="flex-1"
                />
                <Select 
                  value={field.field_type} 
                  onValueChange={(value) => {
                    const updated = testFields.map(f => 
                      f.id === field.id ? { ...f, field_type: value } : f
                    );
                    setTestFields(updated);
                  }}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="date">Datum</SelectItem>
                    <SelectItem value="number">Číslo</SelectItem>
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-1 text-xs text-slate-500">
                  <Switch
                    checked={field.required}
                    onCheckedChange={(checked) => {
                      const updated = testFields.map(f => 
                        f.id === field.id ? { ...f, required: checked } : f
                      );
                      setTestFields(updated);
                    }}
                  />
                  Povinné
                </label>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleUpdateTestField(field)}
                  className="opacity-0 group-hover:opacity-100"
                >
                  <Save className="h-4 w-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => handleDeleteTestField(field.id)}
                  className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Add new test field */}
          <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-dashed border-purple-200">
            <Input
              placeholder="Název nového pole"
              value={newTestField.name}
              onChange={(e) => setNewTestField({ ...newTestField, name: e.target.value })}
              className="flex-1"
            />
            <Select 
              value={newTestField.field_type} 
              onValueChange={(value) => setNewTestField({ ...newTestField, field_type: value })}
            >
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="date">Datum</SelectItem>
                <SelectItem value="number">Číslo</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1 text-xs text-slate-500">
              <Switch
                checked={newTestField.required}
                onCheckedChange={(checked) => setNewTestField({ ...newTestField, required: checked })}
              />
              Povinné
            </label>
            <Button onClick={handleAddTestField} className="bg-purple-500 hover:bg-purple-600">
              <Plus className="h-4 w-4 mr-1" />
              Přidat
            </Button>
          </div>
        </div>

        {/* Evaluation Fields Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
              <FlaskConical className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">
                Pole pro hodnocení {sensoryType === 'krabicky' ? '(Skupina B - základní)' : ''}
              </h2>
              <p className="text-sm text-slate-500">
                {sensoryType === 'krabicky' 
                  ? 'Parametry viditelné všem hodnotitelům (A/B/C)'
                  : 'Parametry hodnocené při senzorické zkoušce (A/B/C)'
                }
              </p>
            </div>
          </div>

          {/* Existing eval fields - Group B (base fields) */}
          <div className="space-y-3 mb-6">
            {evalFields.filter(f => !f.is_group_a_only).map((field) => (
              <div key={field.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg group">
                <GripVertical className="h-4 w-4 text-slate-400" />
                <Input
                  value={field.name}
                  onChange={(e) => {
                    const updated = evalFields.map(f => 
                      f.id === field.id ? { ...f, name: e.target.value } : f
                    );
                    setEvalFields(updated);
                  }}
                  className="flex-1"
                />
                <label className="flex items-center gap-1 text-xs text-slate-500">
                  <Switch
                    checked={field.is_active}
                    onCheckedChange={(checked) => {
                      const updated = evalFields.map(f => 
                        f.id === field.id ? { ...f, is_active: checked } : f
                      );
                      setEvalFields(updated);
                    }}
                  />
                  Aktivní
                </label>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => handleUpdateEvalField(field)}
                  className="opacity-0 group-hover:opacity-100"
                >
                  <Save className="h-4 w-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => handleDeleteEvalField(field.id)}
                  className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Add new eval field */}
          <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-dashed border-emerald-200">
            <Input
              placeholder="Název nového hodnotícího pole"
              value={newEvalField.name}
              onChange={(e) => setNewEvalField({ ...newEvalField, name: e.target.value })}
              className="flex-1"
            />
            <Button onClick={handleAddEvalField} className="bg-emerald-500 hover:bg-emerald-600">
              <Plus className="h-4 w-4 mr-1" />
              Přidat
            </Button>
          </div>
        </div>

        {/* Group A Fields Section - Only for Krabicky */}
        {sensoryType === 'krabicky' && (
          <div className="bg-white rounded-xl border border-purple-200 p-6">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-purple-100">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-violet-100 flex items-center justify-center">
                <FlaskConical className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Pole pro hodnocení (Skupina A - rozšířená)</h2>
                <p className="text-sm text-slate-500">Dodatečné parametry viditelné pouze členům skupiny A</p>
              </div>
            </div>

            {/* Existing Group A eval fields */}
            <div className="space-y-3 mb-6">
              {evalFields.filter(f => f.is_group_a_only).map((field) => (
                <div key={field.id} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg group">
                  <GripVertical className="h-4 w-4 text-purple-400" />
                  <Input
                    value={field.name}
                    onChange={(e) => {
                      const updated = evalFields.map(f => 
                        f.id === field.id ? { ...f, name: e.target.value } : f
                      );
                      setEvalFields(updated);
                    }}
                    className="flex-1"
                  />
                  <label className="flex items-center gap-1 text-xs text-slate-500">
                    <Switch
                      checked={field.is_active}
                      onCheckedChange={(checked) => {
                        const updated = evalFields.map(f => 
                          f.id === field.id ? { ...f, is_active: checked } : f
                        );
                        setEvalFields(updated);
                      }}
                    />
                    Aktivní
                  </label>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleUpdateEvalField(field)}
                    className="opacity-0 group-hover:opacity-100"
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => handleDeleteEvalField(field.id)}
                    className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {evalFields.filter(f => f.is_group_a_only).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">
                  Zatím nejsou definována žádná pole pro skupinu A
                </p>
              )}
            </div>

            {/* Add new Group A eval field */}
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-dashed border-purple-300">
              <Input
                placeholder="Název nového pole pro skupinu A"
                value={newEvalField.name}
                onChange={(e) => setNewEvalField({ ...newEvalField, name: e.target.value })}
                className="flex-1"
              />
              <Button 
                onClick={() => handleAddEvalField(true)} 
                className="bg-purple-500 hover:bg-purple-600"
              >
                <Plus className="h-4 w-4 mr-1" />
                Přidat pro skupinu A
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SensoryAdminPage;
